import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import SectionLines from "../common/SectionLines";
import Popup from "../common/Popup";
import ReportIssueForm from "../lyrics/ReportIssueForm";
import { loadSong } from "../../actions/catalogActions";
import { addSongToLibrary, removeSongFromLibrary } from "../../actions/libraryActions";
import { setQueueTranspose, queueSongNext } from "../../actions/queueActions";
import { selectSong } from "../../actions/sessionActions";
import { setShowChords, setSongKey } from "../../actions/preferencesActions";
import { bookmarkSong } from "../../actions/setsActions";
import { requestSong, getRequestName } from "../../actions/requestsActions";
import {
  selectAnySong,
  selectIsHost,
  selectIsInLibrary,
  selectIsInQueue,
  selectIsInSet,
  selectLastUsedSet,
  selectTranspose,
} from "../../selectors";
import { songHasChords, sortedVerses, verseLabel } from "../../lib/lyrics";
import { describeTranspose, transposeNote } from "../../lib/chords";
import { MAX_SEMITONES, MIN_SEMITONES } from "../../lib/preferences";

/**
 * One song: its sections with chords on demand and the key stepper, favourite and edit,
 * add to a set and go live (hosts) or request it (guests).
 */
const SongPage = () => {
  const { id } = useParams();
  const songId = Number(id);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const song = useSelector((state) => selectAnySong(state, songId));
  const isHost = useSelector(selectIsHost);
  const host = useSelector((state) => state.host);
  const code = useSelector((state) => state.session.Code);
  const inLibrary = useSelector((state) => selectIsInLibrary(state, songId));
  const inQueue = useSelector((state) => selectIsInQueue(state, songId));
  const showChords = useSelector((state) => state.preferences.showChords);
  const transpose = useSelector((state) => selectTranspose(state, songId));
  const lastSet = useSelector(selectLastUsedSet);
  const inLastSet = useSelector((state) => (lastSet ? selectIsInSet(state, lastSet.Id, songId) : false));
  const [reporting, setReporting] = useState(false);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (!song || !song.Verses) {
      dispatch(loadSong(songId)).then((loaded) => {
        if (!loaded) setMissing(true);
      });
    }
  }, [dispatch, songId, song]);

  if (missing) {
    return (
      <div className="page">
        <button type="button" className="back-link" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <div className="empty">That song is no longer available.</div>
      </div>
    );
  }

  if (!song) {
    return (
      <div className="page">
        <div className="loading">Loading…</div>
      </div>
    );
  }

  const verses = sortedVerses(song);
  const hasChords = songHasChords(song);
  const canEdit = isHost && host.hasKey && (song.OwnerId == null ? host.canPublish : song.OwnerId === host.userId);
  const keyLabel = song.Key ? `Key ${transposeNote(song.Key, transpose)}${transpose ? ` ${describeTranspose(transpose)}` : ""}` : `${describeTranspose(transpose) || "0"} semitones`;
  const metaParts = [song.Artist, verses.length ? `${verses.length} ${verses.length === 1 ? "section" : "sections"}` : null].filter(Boolean);

  const stepKey = (delta) => {
    const next = Math.max(MIN_SEMITONES, Math.min(MAX_SEMITONES, transpose + delta));
    dispatch(setSongKey(songId, next));
    // the host's choice is what the room sings in
    if (isHost && inQueue) dispatch(setQueueTranspose(code, songId, next));
  };

  const goLive = async () => {
    const queued = inQueue ? { Id: songId } : await dispatch(queueSongNext(code, songId, { atEnd: false, semitones: transpose, quiet: true }));
    if (!queued) return;
    dispatch(selectSong(code, songId, verses[0]?.Order ?? 0));
    navigate("/lyrics");
  };

  const request = () => dispatch(requestSong(code, song, getRequestName()));

  return (
    <div className="page" data-screen-label="Song">
      <button type="button" className="back-link" onClick={() => navigate(-1)}>
        ← Back
      </button>
      <h2 className="page-title" style={{ fontSize: 26 }}>
        {song.Title}
        {song.OwnerId != null && <span className="private-mark">private</span>}
      </h2>
      {metaParts.length > 0 && <div className="page-sub">{metaParts.join(" · ")}</div>}
      {song.Key && (
        <p className="note">
          {transpose ? `Adding it to a set keeps ${transposeNote(song.Key, transpose)}. Written in ${song.Key}.` : `Written in ${song.Key}. Change it and the set remembers your key.`}
          {song.Capo ? ` Capo ${song.Capo}.` : ""}
        </p>
      )}
      <div className="wrap">
        <button type="button" className="btn btn-sm btn-outline" aria-pressed={showChords} onClick={() => dispatch(setShowChords(!showChords))} disabled={!hasChords} title={hasChords ? undefined : "This song has no chords"}>
          Chords
        </button>
        <div className="stepper" role="group" aria-label="Key">
          <button type="button" onClick={() => stepKey(-1)} aria-label="Lower the key" disabled={transpose <= MIN_SEMITONES}>
            −
          </button>
          <span>{keyLabel}</span>
          <button type="button" onClick={() => stepKey(1)} aria-label="Raise the key" disabled={transpose >= MAX_SEMITONES}>
            ＋
          </button>
        </div>
        {isHost && (
          <button type="button" className="btn btn-round btn-sm btn-outline" aria-pressed={inLibrary} aria-label={inLibrary ? "Remove from favourites" : "Add to favourites"} onClick={() => dispatch(inLibrary ? removeSongFromLibrary(songId) : addSongToLibrary(songId))}>
            {inLibrary ? "♥" : "♡"}
          </button>
        )}
        {canEdit && (
          <button type="button" className="btn btn-sm btn-outline" onClick={() => navigate(`/songs/${songId}/edit`)}>
            Edit
          </button>
        )}
      </div>

      <div className="section-list">
        {verses.map((verse, index) => (
          <div key={verse.Order} className="section">
            <span className="kicker">{verseLabel(verse, index)}</span>
            <SectionLines verse={verse} showChords={showChords && hasChords} transpose={transpose} size="sm" />
          </div>
        ))}
        {!song.Verses && <div className="loading">Loading the lyrics…</div>}
      </div>

      <button type="button" className="btn-ghost small" onClick={() => setReporting(true)} style={{ alignSelf: "flex-start" }}>
        Report a problem with this song
      </button>

      <div style={{ flex: 1 }} />
      {isHost ? (
        <div className="btn-row">
          {lastSet && host.hasKey && (
            <button type="button" className="btn btn-md btn-outline" onClick={() => dispatch(bookmarkSong(lastSet.Id, song))} disabled={inLastSet}>
              {inLastSet ? `In ${lastSet.Name}` : `Add to ${lastSet.Name}`}
            </button>
          )}
          <button type="button" className="btn btn-md btn-primary" onClick={goLive}>
            Go live
          </button>
        </div>
      ) : (
        <button type="button" className="btn btn-md btn-primary btn-block" onClick={request}>
          Request this song
        </button>
      )}

      {reporting && (
        <Popup onClose={() => setReporting(false)} title="Report a problem">
          <ReportIssueForm song={song} onDone={() => setReporting(false)} />
        </Popup>
      )}
    </div>
  );
};

export default SongPage;
